import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import BarcodeScannerModal from './BarcodeScannerModal';
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from '../utils/haptics';
import API_BASE_URL from '../config';
import './BottomNav.css';

const BottomNav = ({ onCartClick, onOpenCategoryMenu, onSearchClick, isCartOpen = false, onCloseCart, scanTrigger }) => {
  const [showScanner, setShowScanner] = useState(false);
  const [notFoundEan, setNotFoundEan] = useState('');
  const [isEanLookupLoading, setIsEanLookupLoading] = useState(false);
  const { cartItems, lastAdded } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Allow external components (e.g. HomeScreen scan tile) to trigger the scanner
  useEffect(() => {
    if (scanTrigger > 0) setShowScanner(true);
  }, [scanTrigger]);

  const handleScanResult = async (ean) => {
    setShowScanner(false);
    const scannedEan = (ean || '').trim();
    if (!scannedEan) return;
    setIsEanLookupLoading(true);
    try {
      try {
        const res = await axios.get(`${API_BASE_URL}/products/by-ean/${encodeURIComponent(scannedEan)}`);
        if (res.data?.id) {
          hapticSuccess();
          navigate(`/product/${res.data.id}`);
          return;
        }
      } catch (_) {}
      hapticError();
      setNotFoundEan(scannedEan);
    } finally {
      setIsEanLookupLoading(false);
    }
  };

  const isHome = location.pathname === '/';
  const isSearch = location.pathname === '/products';
  const isAccount = location.pathname === '/account';

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || null;

  return (
    <>
      <div className="bottom-nav" role="navigation" aria-label="Navegação principal">
        {/* Glass dock */}
        <nav className="bottom-nav-dock">
          {/* Início */}
          <button
            className={`bottom-nav-tab${isHome ? ' bottom-nav-tab--active' : ''}`}
            onClick={() => { hapticLight(); navigate('/'); }}
            aria-label="Início"
          >
            <svg className="bottom-nav-tab-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                fill={isHome ? 'currentColor' : 'none'} fillOpacity={isHome ? 0.15 : 0}
              />
              <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="bottom-nav-tab-label">Início</span>
          </button>

          {/* Pesquisar */}
          <button
            className={`bottom-nav-tab${isSearch ? ' bottom-nav-tab--active' : ''}`}
            onClick={() => { hapticLight(); if (onSearchClick) onSearchClick(); else navigate('/products'); }}
            aria-label="Pesquisar produtos"
          >
            <svg className="bottom-nav-tab-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"
                fill={isSearch ? 'currentColor' : 'none'} fillOpacity={isSearch ? 0.12 : 0}
              />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span className="bottom-nav-tab-label">Pesquisar</span>
          </button>

          {/* Center gap for FAB */}
          <div className="bottom-nav-fab-gap" aria-hidden="true" />

          {/* Carrinho */}
          <button
            className={`bottom-nav-tab${lastAdded ? ' bottom-nav-tab--cart-pulse' : ''}`}
            onClick={() => { hapticLight(); onCartClick(); }}
            data-cart-anchor="true"
            aria-label={`Carrinho, ${cartItems.length} ${cartItems.length === 1 ? 'produto' : 'produtos'}`}
          >
            <svg className="bottom-nav-tab-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 4H5L7 14H18L21 7H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="9" cy="19" r="1.4" fill="currentColor" />
              <circle cx="18" cy="19" r="1.4" fill="currentColor" />
            </svg>
            {cartItems.length > 0 && (
              <span className="bottom-nav-badge" aria-hidden="true">{cartItems.length}</span>
            )}
            <span className="bottom-nav-tab-label">Carrinho</span>
          </button>

          {/* Conta */}
          <button
            className={`bottom-nav-tab${isAccount ? ' bottom-nav-tab--active' : ''}`}
            onClick={() => { hapticLight(); navigate('/account'); }}
            aria-label="Conta"
          >
            {initials ? (
              <div className="bottom-nav-avatar" aria-hidden="true">{initials}</div>
            ) : (
              <svg className="bottom-nav-tab-icon" viewBox="0 0 24 24"
                fill={isAccount ? 'currentColor' : 'none'} fillOpacity={isAccount ? 0.12 : 0} aria-hidden="true">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
            <span className="bottom-nav-tab-label">Conta</span>
          </button>
        </nav>

        {/* Scan FAB — sibling of dock, centered above it */}
        <button
          className="bottom-nav-fab"
          onClick={() => { hapticMedium(); setShowScanner(true); }}
          aria-label="Digitalizar código de barras"
          disabled={isEanLookupLoading}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 7V5a2 2 0 012-2h2M21 7V5a2 2 0 00-2-2h-2M3 17v2a2 2 0 002 2h2M21 17v2a2 2 0 01-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M7.5 8v8M10.5 8v8M13.5 8v8M16.5 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <BarcodeScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onSearch={handleScanResult}
      />

      {notFoundEan && (
        <div className="barcode-popup-overlay" onClick={() => setNotFoundEan('')}>
          <div className="barcode-popup" onClick={(e) => e.stopPropagation()}>
            <div className="barcode-popup-header">
              <h3>Produto não encontrado</h3>
              <button className="barcode-popup-close-x" onClick={() => setNotFoundEan('')} aria-label="Fechar">✕</button>
            </div>
            <p>Nenhum produto encontrado para o código <strong>{notFoundEan}</strong>.</p>
          </div>
        </div>
      )}

      {isEanLookupLoading && (
        <div className="barcode-popup-overlay" role="status" aria-live="polite">
          <div className="barcode-popup barcode-loader-popup" onClick={(e) => e.stopPropagation()}>
            <div className="barcode-loader-spinner" aria-hidden="true" />
            <h3>A pesquisar…</h3>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;
